import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, MessageSquare, Calendar, Sparkles } from 'lucide-react';
interface NotificationPromptProps {
  role: 'brand' | 'organizer';
}
export function NotificationPrompt({
  role
}: NotificationPromptProps) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    // Check if already dismissed or enabled
    const hasPrompted = localStorage.getItem('sponsrai_notification_prompted');
    if (!hasPrompted) {
      // Show after 3 seconds
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);
  const handleEnable = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        localStorage.setItem('sponsrai_notifications_enabled', 'true');
      }
    } catch (err) {
      console.log('Notifications not supported');
    }
    localStorage.setItem('sponsrai_notification_prompted', 'true');
    setShow(false);
  };
  const handleDismiss = () => {
    localStorage.setItem('sponsrai_notification_prompted', 'true');
    setShow(false);
  };
  const accentColor = role === 'brand' ? 'cyan' : 'purple';
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  return <AnimatePresence data-id="element-326">
      {show && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-327">
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden" data-id="element-328">
            <div className="p-6" data-id="element-329">
              <button onClick={handleDismiss} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-330">
                <X size={20} data-id="element-331" />
              </button>

              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${role === 'brand' ? 'bg-cyan-500/20' : 'bg-purple-500/20'}`} data-id="element-332">
                <Bell size={32} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-333" />
              </div>

              <h2 className="text-2xl font-bold text-white text-center mb-3" data-id="element-334">
                Don't miss your matches!
              </h2>
              <p className="text-slate-400 text-center mb-6" data-id="element-335">
                Enable notifications to get instant alerts when:
              </p>

              <div className="space-y-3 mb-8" data-id="element-336">
                <div className="flex items-center gap-3 text-slate-300" data-id="element-337">
                  <div className={`p-2 rounded-lg ${role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`} data-id="element-338">
                    <Sparkles size={18} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-339" />
                  </div>
                  <span data-id="element-340">New matches are found for you</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300" data-id="element-341">
                  <div className={`p-2 rounded-lg ${role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`} data-id="element-342">
                    <MessageSquare size={18} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-343" />
                  </div>
                  <span data-id="element-344">Someone sends you a message</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300" data-id="element-345">
                  <div className={`p-2 rounded-lg ${role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10'}`} data-id="element-346">
                    <Calendar size={18} className={role === 'brand' ? 'text-cyan-400' : 'text-purple-400'} data-id="element-347" />
                  </div>
                  <span data-id="element-348">Meeting invites and reminders</span>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-349">
                <button onClick={handleDismiss} className="flex-1 py-3 rounded-full font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" data-id="element-350">
                  Maybe later
                </button>
                <button onClick={handleEnable} className={`flex-1 py-3 rounded-full font-bold transition-all flex items-center justify-center gap-2 ${buttonColor}`} data-id="element-351">
                  <Bell size={18} data-id="element-352" /> Enable
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}